import { useState } from 'react';
import { MembershipTier } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { stripe, createSubscriptionCheckout } from '../lib/stripe';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export function useUpgradeFlow() {
  const [loading, setLoading] = useState(false);
  const { profile, updateProfile } = useAuth();
  const navigate = useNavigate();

  const upgradeTier = async (newTier: MembershipTier, priceId: string) => {
    if (!profile) {
      toast.error('Please sign in to upgrade your subscription');
      return;
    }

    try {
      setLoading(true);

      // In development, simulate the upgrade process
      if (import.meta.env.DEV) {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Simulate updating the profile
        await supabase
          .from('profiles')
          .update({
            membership_tier: newTier,
            subscription_status: 'active',
            subscription_period_start: new Date().toISOString(),
            subscription_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          })
          .eq('id', profile.id);

        // Update local profile data
        await updateProfile();

        // Navigate to success page
        navigate('/subscription/success');
        return;
      }

      // Production flow
      const { data: planData, error: planError } = await supabase
        .from('subscription_plans')
        .select('stripe_price_id_monthly')
        .eq('tier', newTier)
        .single();

      if (planError) throw planError;
      if (!planData?.stripe_price_id_monthly) {
        throw new Error('Invalid subscription plan');
      }

      const { sessionId, error } = await createSubscriptionCheckout(
        planData.stripe_price_id_monthly,
        profile.stripe_customer_id
      );

      if (error) throw error;
      if (!sessionId) throw new Error('Failed to create checkout session');

      const stripeInstance = await stripe;
      if (!stripeInstance) throw new Error('Failed to load Stripe');

      const { error: redirectError } = await stripeInstance.redirectToCheckout({
        sessionId,
      });

      if (redirectError) throw redirectError;
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast.error('Failed to start upgrade process');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    upgradeTier
  };
}